import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError, LEGACY_TOKEN_KEY, setOnAuthExpired } from "@/lib/api";

const AuthContext = createContext(null);

// Migration nicety: purge V1.2 localStorage token so an old client session
// doesn't linger. One-time UX blip; user re-logs in cleanly.
try {
  if (typeof window !== "undefined") {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  }
} catch {
  /* SSR / private mode — ignore */
}

export function AuthProvider({ children }) {
  // status: "loading" | "authed" | "guest"
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);
  const [permissionMatrix, setPermissionMatrix] = useState(null);

  const loadMe = useCallback(async () => {
    // Batch V1.3 — no localStorage token to gate on. Just call /auth/me;
    // if the cookie is present + valid it returns 200, else the response
    // interceptor tries a silent refresh, else we land in the catch.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setStatus("authed");
    } catch {
      setUser(null);
      setStatus("guest");
    }
  }, []);

  useEffect(() => {
    setOnAuthExpired(() => {
      setUser(null);
      setStatus("guest");
    });
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    api.get("/permissions").then(({ data }) => setPermissionMatrix(data)).catch(() => {});
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      const { data } = await api.post("/auth/login", { username, password });
      setUser(data.user);
      setStatus("authed");
      return { ok: true, user: data.user };
    } catch (err) {
      if (err?.response?.status === 429) {
        const retryAfter = parseInt(err.response.headers?.["retry-after"] ?? "0", 10);
        const seconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 900;
        return {
          ok: false,
          error: err.response?.data?.detail || "Too many failed login attempts.",
          lockout: { retry_after_seconds: seconds },
        };
      }
      return { ok: false, error: formatApiError(err) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* stateless — ignore */
    }
    try { localStorage.removeItem(LEGACY_TOKEN_KEY); } catch { /* ignore */ }
    setUser(null);
    setStatus("guest");
  }, []);

  const hasPermission = useCallback(
    (perm) => Boolean(user?.permissions?.includes(perm)),
    [user],
  );

  return (
    <AuthContext.Provider
      value={{ status, user, permissionMatrix, login, logout, hasPermission, reload: loadMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
